/**
 * Staff Console -> Custom Plans tab (meeting 2026-09-28).
 *
 * The Custom plan replaced Agency: no list price, so the pipeline is
 * intake form -> call -> quote. Every submission of custom-plan.html lands
 * here with the prospect's answers. Staff move it through the statuses,
 * record the agreed monthly price, and generate a Stripe checkout link at
 * that price (webhooks worker, /billing/admin/custom-plan-checkout). Paying
 * that link puts the account on the Custom tier (internal key 'agency').
 * Once paid (Custom plan or team seat checkout), the webhooks worker marks
 * the row won, stamps paid_at and emails the client their onboarding steps
 * (migration 0052); the Onboarding box shows that and can resend the email.
 */

import { useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import { Loader2, RefreshCw, Building2, Check, Copy, CreditCard, ChevronDown, ChevronRight, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { fmtLocale } from '@/lib/dates'
import { isStaffUser } from './staff'
import {
  useCustomPlanInquiries,
  useUpdateCustomPlanInquiry,
  useCustomPlanCheckout,
  useResendOnboardingEmail,
  type CustomPlanInquiry,
  type CustomPlanStatus,
} from './use-admin'
import { qualifyInquiry } from './custom-plan-qualify'

const ROUTE_TONE: Record<string, string> = {
  custom_build: 'border-[rgba(16,185,129,0.35)] bg-[rgba(16,185,129,0.06)]',
  prebuilt: 'border-[rgba(251,191,36,0.35)] bg-[rgba(251,191,36,0.06)]',
  unknown: 'border-border',
}
const TIER_LABEL: Record<string, string> = { qualified: 'Qualified', maybe: 'Maybe', not_a_fit: 'Not a fit' }

const STATUSES: { key: CustomPlanStatus; label: string; tone: string }[] = [
  { key: 'new', label: 'New', tone: 'bg-[rgba(248,113,113,0.12)] text-[#F87171]' },
  { key: 'contacted', label: 'Contacted', tone: 'bg-[rgba(251,191,36,0.12)] text-[#FBBF24]' },
  { key: 'call_booked', label: 'Call booked', tone: 'bg-[rgba(59,130,246,0.12)] text-[#8FB4FA]' },
  { key: 'quoted', label: 'Quoted', tone: 'bg-[rgba(168,85,247,0.12)] text-[#C4B5FD]' },
  { key: 'won', label: 'Won', tone: 'bg-[rgba(16,185,129,0.12)] text-[#34D399]' },
  { key: 'lost', label: 'Lost', tone: 'bg-[rgba(255,255,255,0.06)] text-[var(--color-silver)]' },
]

/** Mirrors NEED_OPTIONS in workers/campaigns/src/routes/custom-plan-inquiry.ts. */
const NEED_LABELS: Record<string, string> = {
  multiple_reps: 'Several reps',
  higher_volume: 'Higher volume',
  custom_messaging: 'Custom messaging',
  booking: 'Calendar booking',
  crm: 'CRM integration',
  content: 'Content',
  client_accounts: 'Client accounts',
  done_for_you: 'Done for you',
}

function parseNeeds(raw: string | null): string[] {
  if (!raw) return []
  try {
    const v = JSON.parse(raw) as unknown
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

function statusMeta(s: string) {
  return STATUSES.find((x) => x.key === s) ?? STATUSES[0]!
}

function InquiryCard({ q, canEdit }: { q: CustomPlanInquiry; canEdit: boolean }) {
  const [open, setOpen] = useState(q.status === 'new')
  const [notes, setNotes] = useState(q.staff_notes ?? '')
  const [price, setPrice] = useState(q.quote_usd != null ? String(q.quote_usd) : '')
  const [budget, setBudget] = useState(q.budget ?? '')
  const qual = qualifyInquiry(q)
  const [link, setLink] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const update = useUpdateCustomPlanInquiry()
  const checkout = useCustomPlanCheckout()
  const resend = useResendOnboardingEmail()
  const meta = statusMeta(q.status)
  const needs = parseNeeds(q.needs)
  const priceNum = Number(price)
  const priceOk = price.trim() !== '' && Number.isFinite(priceNum) && priceNum >= 1

  function copy(text: string) {
    void navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-start justify-between gap-3 text-left">
          <div className="min-w-0">
            <CardTitle className="flex flex-wrap items-center gap-2 text-sm font-semibold">
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <Building2 className="h-4 w-4 text-[#34D399]" />
              {q.company}
              <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-medium', meta.tone)}>{meta.label}</span>
              {qual.tier && (
                <span className="rounded border border-border px-1.5 py-0.5 text-[10px] font-medium">
                  {TIER_LABEL[qual.tier]} {qual.score}/10
                </span>
              )}
              {q.paid_at && (
                <span className="rounded bg-[rgba(16,185,129,0.12)] px-1.5 py-0.5 text-[10px] font-medium text-[#34D399]">Paid</span>
              )}
              {q.quote_usd != null && (
                <span className="text-xs font-normal text-[var(--color-silver)]">${q.quote_usd.toLocaleString()}/mo</span>
              )}
            </CardTitle>
            <p className="mt-1 truncate text-xs text-[var(--color-silver)]">
              {q.name}
              {q.role ? `, ${q.role}` : ''} · {q.email} · {q.team_size ?? '?'} reps · {fmtLocale(q.created_at)}
            </p>
          </div>
          <Badge variant="outline" className="shrink-0 text-[10px]">
            {q.user_id ? `Has account (${q.user_plan === 'agency' ? 'Custom' : q.user_plan})` : 'No account yet'}
          </Badge>
        </button>
      </CardHeader>

      {open && (
        <CardContent className="space-y-4 pt-0 text-sm">
          <div className="grid grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
            <p><span className="text-[var(--color-silver)]">Website:</span> {q.website ?? '-'}</p>
            <p><span className="text-[var(--color-silver)]">Meetings/mo wanted:</span> {q.meetings_goal ?? '-'}</p>
            <p><span className="text-[var(--color-silver)]">Timeline:</span> {q.timeline ?? '-'}</p>
            <p><span className="text-[var(--color-silver)]">Source:</span> {q.utm_source ?? q.referrer ?? 'direct'}</p>
            <p>
              <span className="text-[var(--color-silver)]">Emails:</span> staff {q.staff_notified_at ? 'sent' : 'NOT sent'}, prospect{' '}
              {q.confirmation_sent_at ? 'sent' : 'not sent'}
            </p>
          </div>
          <div className={cn('space-y-2 rounded-md border p-3', ROUTE_TONE[qual.route])}>
            <p className="text-xs font-semibold">
              Qualification: {qual.route === 'custom_build' ? 'custom build' : qual.route === 'prebuilt' ? 'prebuilt, not custom' : 'budget unknown'}
            </p>
            <p className="text-[11px] text-[var(--color-silver)]">{qual.reason}</p>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                placeholder="Budget from the call, e.g. $1,500/mo"
                disabled={!canEdit}
                className="h-8 w-[220px] text-xs"
              />
              {canEdit && budget !== (q.budget ?? '') && (
                <Button size="sm" className="h-8 text-xs" disabled={update.isPending} onClick={() => update.mutate({ id: q.id, budget })}>
                  Save budget
                </Button>
              )}
            </div>
          </div>
          {needs.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {needs.map((n) => (
                <span key={n} className="rounded border border-border px-1.5 py-0.5 text-[11px]">
                  {NEED_LABELS[n] ?? n}
                </span>
              ))}
            </div>
          )}
          {q.details && (
            <p className="whitespace-pre-wrap rounded-md bg-[rgba(255,255,255,0.03)] p-3 text-xs leading-relaxed">{q.details}</p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={q.status}
              disabled={!canEdit || update.isPending}
              onValueChange={(v) => update.mutate({ id: q.id, status: v as CustomPlanStatus })}
            >
              <SelectTrigger className="h-8 w-[150px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s.key} value={s.key} className="text-xs">
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button asChild size="sm" variant="outline" className="h-8">
              <a href={`mailto:${q.email}?subject=${encodeURIComponent('Your DealCloser custom plan')}`}>Email {q.name.split(' ')[0]}</a>
            </Button>
          </div>

          <div className="space-y-1.5">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={!canEdit}
              rows={2}
              placeholder="Call notes (scope, reps, limits agreed…)"
              className="w-full rounded-md border border-input bg-background p-2 text-xs placeholder:text-subtle-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
            />
            {canEdit && notes !== (q.staff_notes ?? '') && (
              <Button size="sm" className="h-7 text-xs" disabled={update.isPending} onClick={() => update.mutate({ id: q.id, staff_notes: notes })}>
                Save notes
              </Button>
            )}
          </div>

          <div className="space-y-2 rounded-md border border-border p-3">
            <p className="flex items-center gap-1.5 text-xs font-semibold">
              <CreditCard className="h-3.5 w-3.5" /> Quote and checkout link
            </p>
            <p className="text-[11px] text-[var(--color-silver)]">
              Enter the monthly price agreed on the call. The link starts a monthly Stripe subscription at that price and moves the
              account to the Custom plan once paid. {q.user_id ? '' : 'They need to sign up with this email first.'}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs">$</span>
              <Input
                value={price}
                onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ''))}
                placeholder="Monthly price"
                disabled={!canEdit}
                className="h-8 w-[130px] text-xs"
              />
              <span className="text-xs text-[var(--color-silver)]">/mo</span>
              <Button
                size="sm"
                className="h-8"
                disabled={!canEdit || !priceOk || checkout.isPending}
                onClick={() =>
                  checkout.mutate(
                    { email: q.email, monthly_usd: priceNum, inquiry_id: q.id },
                    { onSuccess: (d) => { setLink(d.url); setCopied(false) } },
                  )
                }
              >
                {checkout.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                Generate checkout link
              </Button>
            </div>
            {checkout.isError && (
              <p className="text-xs text-red-400">{checkout.error instanceof Error ? checkout.error.message : 'Failed'}</p>
            )}
            {link && (
              <button
                type="button"
                onClick={() => copy(link)}
                className="flex max-w-full items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs text-emerald-300 hover:bg-emerald-500/20"
                title="Copy link"
              >
                <span className="truncate">{link}</span>
                {copied ? <Check className="h-3 w-3 shrink-0" /> : <Copy className="h-3 w-3 shrink-0" />}
              </button>
            )}
          </div>
          {q.paid_at && (
            <div className="space-y-2 rounded-md border border-[rgba(16,185,129,0.35)] bg-[rgba(16,185,129,0.06)] p-3">
              <p className="flex items-center gap-1.5 text-xs font-semibold">
                <Mail className="h-3.5 w-3.5" /> Onboarding
              </p>
              <div className="grid grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
                <p><span className="text-[var(--color-silver)]">Paid:</span> {fmtLocale(q.paid_at)}</p>
                <p>
                  <span className="text-[var(--color-silver)]">Onboarding email:</span>{' '}
                  {q.onboarding_sent_at ? `sent ${fmtLocale(q.onboarding_sent_at)}` : 'NOT sent'}
                </p>
                <p><span className="text-[var(--color-silver)]">Sent to:</span> {q.onboarding_email ?? q.email}</p>
                {q.org_id && <p><span className="text-[var(--color-silver)]">Team org:</span> {q.org_id}</p>}
              </div>
              {canEdit && (
                <Button size="sm" variant="outline" className="h-8 text-xs" disabled={resend.isPending} onClick={() => resend.mutate(q.id)}>
                  {resend.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                  Resend onboarding email
                </Button>
              )}
              {resend.isSuccess && <p className="text-xs text-emerald-300">Sent to {resend.data.sent_to}.</p>}
              {resend.isError && (
                <p className="text-xs text-red-400">{resend.error instanceof Error ? resend.error.message : 'Failed'}</p>
              )}
            </div>
          )}
          {update.isError && (
            <p className="text-xs text-red-400">{update.error instanceof Error ? update.error.message : 'Update failed'}</p>
          )}
        </CardContent>
      )}
    </Card>
  )
}

export function CustomPlansPage() {
  const { user } = useUser()
  const canEdit = isStaffUser(user?.id)
  const { data, isLoading, refetch, isRefetching, error } = useCustomPlanInquiries()
  const [filter, setFilter] = useState<'open' | 'all'>('open')

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12 text-[var(--color-silver)]">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading custom plan inquiries…
      </div>
    )
  }
  if (error || !data) {
    return <p className="p-6 text-sm text-red-400">{error instanceof Error ? error.message : 'Failed to load.'}</p>
  }

  const rows = filter === 'open' ? data.filter((q) => q.status !== 'won' && q.status !== 'lost') : data
  const count = (s: CustomPlanStatus) => data.filter((q) => q.status === s).length

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <span key={s.key} className={cn('rounded-md px-2.5 py-1 text-xs font-medium', s.tone)}>
              {s.label}: {count(s.key)}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={(v) => setFilter(v as 'open' | 'all')}>
            <SelectTrigger className="h-8 w-[130px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open" className="text-xs">Open only</SelectItem>
              <SelectItem value="all" className="text-xs">All</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="h-8" onClick={() => void refetch()} disabled={isRefetching}>
            <RefreshCw className={cn('h-3.5 w-3.5', isRefetching && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-[var(--color-silver)]">
            No {filter === 'open' ? 'open ' : ''}custom plan inquiries yet. They arrive from the form at
            dealcloserautopilot.com/custom-plan.
          </CardContent>
        </Card>
      ) : (
        rows.map((q) => <InquiryCard key={q.id} q={q} canEdit={canEdit} />)
      )}
    </div>
  )
}
